import { ReactNode } from 'react';
import { ImageSquare, Trash } from '@phosphor-icons/react';
import { Accept, useDropzone } from 'react-dropzone';

/** 15 MB upload ceiling — matches the legacy CustomImageUpload dropzones. */
const MAX_IMAGE_BYTES = 15 * 1024 * 1024;

/** Default single-image prompt — identical to the legacy asset dropzone copy. */
const DEFAULT_PROMPT: ReactNode = (
  <>
    Drag an image here or{' '}
    <span className="cursor-pointer text-blue-500 hover:underline">browse</span>{' '}
    to choose an image
  </>
);

export interface ImageUploadProps {
  onDrop: (acceptedFiles: File[]) => void;
  accept: Accept;
  label?: string;
  errorMessage?: string;
  successMessage?: string;
  uploadedFileNames: string[];
  id?: string;
  // react-hook-form `register(...)` spread; optional, mirrors the legacy prop.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register?: any;
  /** Edit mode: when set, shows the current image with a remove control
   * instead of the dropzone (superset of the legacy EditAsset variant). */
  currentImageUrl?: string;
  onRemoveImage?: () => void;
  /** Allow selecting several files (RequestCashAdvance invoices). Default false. */
  multiple?: boolean;
  /** Main dropzone prompt. Defaults to the single-image copy. */
  promptText?: ReactNode;
  /** Prefix for the uploaded-files line. Default 'Uploaded File'. */
  uploadedFilesLabel?: string;
}

/**
 * THT-643 — single shared image dropzone. Replaces the three duplicate
 * `CustomImageUpload` definitions (CreateAsset, EditAsset, and
 * banking/RequestCashAdvance). It is the superset of those copies: when
 * `currentImageUrl` is provided it renders the existing image with a remove
 * button (the EditAsset behavior); otherwise it renders the dropzone. Markup
 * and styling are copied verbatim so existing screens render identically.
 */
export default function ImageUpload({
  onDrop,
  accept,
  label,
  errorMessage,
  successMessage,
  uploadedFileNames,
  id,
  register,
  currentImageUrl,
  onRemoveImage,
  multiple = false,
  promptText = DEFAULT_PROMPT,
  uploadedFilesLabel = 'Uploaded File',
}: ImageUploadProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: MAX_IMAGE_BYTES,
    multiple,
  });

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}
      {currentImageUrl ? (
        <div className="relative h-[300px] min-h-[300px] w-full overflow-hidden rounded-lg border-2 border-gray-300 bg-white shadow-md">
          <img
            src={currentImageUrl}
            alt="Current asset"
            className="h-full w-full object-contain"
          />
          {onRemoveImage && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                onRemoveImage();
              }}
              className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
            >
              <Trash size={20} />
            </button>
          )}
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`flex h-[300px] min-h-[300px] w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 ${
            isDragActive ? 'border-blue-400' : 'border-dashed border-gray-300'
          } bg-white px-6 py-6 text-center shadow-md transition-all duration-200 ease-in-out hover:shadow-lg`}
        >
          <input id={id} {...getInputProps()} {...register} />
          <div className="flex flex-col items-center">
            <ImageSquare size={48} className="text-[#4A5568]" />
            <p className="mt-2 text-lg text-[#4A5568]">{promptText}</p>
            <p className="text-xs text-gray-500">Max 15 MB file is allowed</p>
          </div>
        </div>
      )}
      {uploadedFileNames &&
        uploadedFileNames.length > 0 &&
        uploadedFileNames[0]?.length > 0 && (
          <p className="mt-2 text-sm text-gray-500">
            {uploadedFilesLabel}: {uploadedFileNames.join(', ')}
          </p>
        )}
      {errorMessage && (
        <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
      )}
      {successMessage && (
        <p className="mt-2 text-sm text-green-500">{successMessage}</p>
      )}
    </div>
  );
}
